/* eslint-disable no-console */
import { useDispatch } from 'react-redux';
import { Box, Button, makeStyles, TextField, Typography } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import FormularioService from '../../API/FormularioService';
import { useAppSelector } from '../../hooks/reduxHooks';
import analyticsService from '../../API/analyticsService';

export const DenunciaComments = ({ comments, denunciaNumber, password, fechaCreacion, onUpdateComment }) => {
  const commentsWithoutLastOne = useMemo(() => {
    if (comments.length === 0) return comments;
    return comments.slice(0, comments.length - 1);
  }, [comments]);
  const lastComment = useMemo(() => {
    if (comments.length === 0) return null;
    return comments[comments.length - 1];
  }, [comments]);

  useEffect(() => {
    const container = document.getElementById('commentsContainer');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [comments]);

  return (
    <Box
      id="commentsContainer"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        backgroundColor: '#FAFBFE',
        border: '1px solid #D8DCDF',
        borderRadius: '8px',
        padding: '12px',
        height: '100%',
        maxHeight: '460px',
        overflowY: 'auto',
      }}
    >
      <DefaultComment denunciaNumber={denunciaNumber} password={password} fechaCreacion={fechaCreacion} />
      {commentsWithoutLastOne.map(item => (
        <Comment key={item.idComentario} item={item} />
      ))}
      {lastComment && (
        <Comment key={lastComment.idComentario} item={lastComment} needResponse onUpdateComment={onUpdateComment} />
      )}
    </Box>
  );
};

const DefaultComment = ({ denunciaNumber, password, fechaCreacion }) => {
  const classes = useStyles();
  return (
    <Box className={classes.commentContainer}>
      <Typography className={classes.titleComment}>
        TEscucha.{' '}
        <Typography component="span" className={classes.text}>
          Recibimos tu denuncia.
        </Typography>
      </Typography>
      <Typography gutterBottom className={classes.text}>
        El equipo de Ética la revisará. Gracias por compartirla con nosotros.
      </Typography>
      <Typography style={{ marginBottom: '16px', marginTop: '16px' }} className={classes.text}>
        Por favor, anota y guarda tu número de denuncia y la contraseña que aparecen a continuación:
      </Typography>
      <Typography fontWeight="bold" className={classes.text}>
        Número de denuncia:{' '}
        <Typography component="span" className={`${classes.bold} ${classes.text}`}>
          {denunciaNumber}
        </Typography>
      </Typography>
      <Typography fontWeight="bold" className={classes.text}>
        Contraseña:{' '}
        <Typography component="span" className={`${classes.bold} ${classes.text}`}>
          {password}
        </Typography>
      </Typography>
      {fechaCreacion && (
        <Typography style={{ fontSize: '0.85rem', marginTop: '5px', color: '#6F7A83' }}>
          {Intl.DateTimeFormat('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }).format(new Date(fechaCreacion))}
        </Typography>
      )}
    </Box>
  );
};

const Comment = ({ item, needResponse, onUpdateComment }) => {
  const { loginData } = useAppSelector(state => state.adminReducer);
  const classes = useStyles();
  const dispatch = useDispatch();
  const [responseText, setResponseText] = useState('');

  const handleSendResponse = useCallback(async () => {
    analyticsService.sendEvent('BTN_ENVIAR_COMENTARIO');

    try {
      const resp = await FormularioService.responseComment({
        dispatch,
        idComentario: item.idComentario,
        comentario: responseText.trim(),
      });

      onUpdateComment?.(resp);
      setResponseText('');

      const container = document.getElementById('commentsContainer');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    } catch (error) {
      console.error('Error sending response:', error);
    }
  }, [responseText, dispatch]);

  return (
    <>
      {needResponse && !item.respuesta && (
        <Box
          sx={{
            margin: '5px auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 'fit-content',
          }}
        >
          <Typography variant="body2" color="textSecondary">
            1 mensaje nuevo
          </Typography>
        </Box>
      )}
      <Box className={classes.commentContainer}>
        <Typography className={classes.titleComment}>
          TEscucha.{' '}
          <Typography component="span" className={classes.text}>
            {item?.comentario}
          </Typography>
        </Typography>
        {item?.fechaComentario && (
          <Typography style={{ fontSize: '0.85rem', marginTop: '5px', color: '#6F7A83' }}>
            {Intl.DateTimeFormat('es-MX', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }).format(new Date(item.fechaComentario))}
          </Typography>
        )}
        {item?.respuesta && (
          <Box
            mt={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 8,
              paddingLeft: '24px',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: '8px',
                top: '-12px',
                bottom: 'calc(100% - 12px)',
                width: '2px',
                backgroundColor: '#232322',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: '8px',
                top: '12px',
                width: '7px',
                height: '2px',
                backgroundColor: '#232322',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: '13px',
                top: '8px',
                width: '6px',
                height: '2px',
                backgroundColor: '#232322',
                transform: 'rotate(45deg)',
                transformOrigin: 'left center',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: '13px',
                top: '16px',
                width: '6px',
                height: '2px',
                backgroundColor: '#232322',
                transform: 'rotate(-45deg)',
                transformOrigin: 'left center',
              }}
            />
            <Box
              sx={{
                backgroundColor: '#FAFBFE',
                borderRadius: '8px',
                padding: '8px 12px',
                flex: 1,
              }}
            >
              <Typography className={`${classes.bold} ${classes.text}`} gutterBottom>
                {loginData?.user?.token ? 'Respuesta' : 'Tu respuesta.'}
              </Typography>
              <Typography style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{item.respuesta}</Typography>
              {item?.fechaRespuesta && (
                <Typography style={{ fontSize: '0.85rem', marginTop: '5px', color: '#6F7A83' }}>
                  {Intl.DateTimeFormat('es-MX', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }).format(new Date(item.fechaRespuesta))}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {needResponse && !item.respuesta && !loginData?.user?.token && (
          <Box
            mt={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              gap: 10,
            }}
          >
            <TextField
              placeholder="Escribe aquí tu respuesta y da clic en “Enviar”"
              fullWidth
              variant="outlined"
              size="small"
              value={responseText}
              onChange={e => setResponseText(e.target.value)}
            />
            <Button
              variant="contained"
              color="primary"
              disableElevation
              disabled={responseText.trim() === ''}
              style={{ minWidth: 90 }}
              onClick={handleSendResponse}
            >
              Enviar
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

const useStyles = makeStyles(() => ({
  titleComment: {
    fontWeight: 'bold',
    fontSize: '0.9rem',
    whiteSpace: 'pre-wrap',
    color: '#333333',
  },
  commentContainer: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '0.5px solid #adb6c0ff',
    padding: '8px 12px',
    fontSize: '0.9rem',
  },
  bold: { fontWeight: 'bold' },
  text: {
    fontSize: '0.9rem',
  },
}));
